export const ALL_MODULES = [
  'dashboard',
  'orders',
  'leads',
  'leads_export',
  'cargo_data',
  'cargo_dispatch',
  'cargo_problems',
  'products',
  'sites',
  'customers',
  'reports',
  'domains',
  'settings',
  'users',
  'blacklist',
  'stock',
  'whatsapp',
  'whatsapp_settings',
] as const

export const MODULE_LABELS: Record<Module, string> = {
  dashboard: 'Anasayfa',
  orders: 'Siparişler',
  leads: 'Leads',
  leads_export: 'Leads Dışa Aktarma',
  cargo_data: 'Kargo Datası',
  cargo_dispatch: 'Kargoya Gönderim',
  cargo_problems: 'Sorunlu Kargolar',
  products: 'Ürünler',
  sites: 'Siteler',
  customers: 'Müşteriler',
  reports: 'Raporlar',
  domains: 'Domainler',
  settings: 'Ayarlar',
  users: 'Kullanıcılar',
  blacklist: 'Kara Liste',
  stock: 'Stok',
  whatsapp: 'WhatsApp',
  whatsapp_settings: 'WhatsApp Ayarları',
}

/**
 * Sadece "Gizle/Göster" (read) yetkisi olan modüller. UI'da sadece read toggle'ı
 * gösterilir; can_write ve can_delete her zaman false saklanır.
 */
export const READ_ONLY_MODULES = new Set<string>(['dashboard', 'cargo_data'])

/** Yalniz execute yetkisi tasiyan, read/write/delete kabul etmeyen moduller. */
export const EXECUTE_ONLY_MODULES = new Set<string>(['cargo_dispatch'])

export type Module = (typeof ALL_MODULES)[number]

export type ModulePermission = {
  module: Module
  can_read: boolean
  can_write: boolean
  can_delete: boolean
  can_execute: boolean
}

export type UserPermissions = {
  tenantUserId: string
  userId: string
  tenantId: string
  isOwner: boolean
  roleId: string | null
  roleName: string | null
  permissions: ModulePermission[]
  roleExclusions: Array<{ resource_type: string; resource_id: string }>
  userExclusions: Array<{ resource_type: string; resource_id: string }>
  // Prompt 87 — /admin/loglar erişimi. Owner her zaman true. Admin için
  // owner tarafından kullanıcı yönetimi sayfasından kapatılabilir. Member
  // rolü hiçbir zaman log sayfasını göremez (false).
  logAccess: boolean
  // Operatör Performans Raporu erişimi. logAccess ile aynı desen: owner her
  // zaman, admin owner'ın verdiği bayrağa göre, member asla.
  operatorReportAccess: boolean
}

/** Kullanici belirtilen modulde okuma yetkisine sahip mi? */
export function canRead(perms: UserPermissions, module: Module): boolean {
  if (perms.isOwner) return true
  return perms.permissions.find((p) => p.module === module)?.can_read ?? false
}

/** Kullanici belirtilen modulde yazma yetkisine sahip mi? */
export function canWrite(perms: UserPermissions, module: Module): boolean {
  if (perms.isOwner) return true
  return perms.permissions.find((p) => p.module === module)?.can_write ?? false
}

/** Kullanici belirtilen modulde silme yetkisine sahip mi? */
export function canDelete(perms: UserPermissions, module: Module): boolean {
  if (perms.isOwner) return true
  return perms.permissions.find((p) => p.module === module)?.can_delete ?? false
}

/** Kullanici belirtilen modulde yuksek riskli islemi calistirabilir mi? */
export function canExecute(perms: UserPermissions, module: Module): boolean {
  if (perms.isOwner) return true
  return perms.permissions.find((p) => p.module === module)?.can_execute ?? false
}

/** Kullanici belirtilen kaynaga erisebilir mi? (exclusion kontrolu) */
export function canAccessResource(
  perms: UserPermissions,
  resourceType: 'product' | 'site' | 'domain',
  resourceId: string
): boolean {
  if (perms.isOwner) return true

  const isRoleExcluded = perms.roleExclusions.some(
    (e) => e.resource_type === resourceType && e.resource_id === resourceId
  )
  if (isRoleExcluded) return false

  const isUserExcluded = perms.userExclusions.some(
    (e) => e.resource_type === resourceType && e.resource_id === resourceId
  )
  if (isUserExcluded) return false

  return true
}

/**
 * Prompt 87 — Kullanıcı /admin/loglar sayfasını görebilir mi?
 * Owner her zaman görür. Admin rolü için `log_access` kolonu
 * kontrol edilir (default true, owner kapatabilir). Member rolü
 * hiçbir zaman göremez.
 */
export function canViewLogs(perms: UserPermissions): boolean {
  if (perms.isOwner) return true
  // Sadece 'Admin' rolü için log_access anlamlı. Custom member rolleri
  // (roleName başka bir değerdir) log sayfasını göremez.
  if (perms.roleName === 'Admin') return perms.logAccess
  return false
}

/**
 * Kullanıcı Operatör Performans Raporu sayfasını görebilir mi?
 * canViewLogs ile aynı desen: Owner her zaman görür, Admin rolü için
 * `operator_report_access` kolonu kontrol edilir, Member asla göremez.
 */
export function canViewOperatorReport(perms: UserPermissions): boolean {
  if (perms.isOwner) return true
  if (perms.roleName === 'Admin') return perms.operatorReportAccess
  return false
}

/**
 * Kaynak listesini exclusion'lara gore filtreler.
 */
export function filterExcludedResources<T extends { id: string }>(
  perms: UserPermissions,
  resources: T[],
  resourceType: 'product' | 'site' | 'domain'
): T[] {
  if (perms.isOwner) return resources

  const excludedIds = new Set([
    ...perms.roleExclusions
      .filter((e) => e.resource_type === resourceType)
      .map((e) => e.resource_id),
    ...perms.userExclusions
      .filter((e) => e.resource_type === resourceType)
      .map((e) => e.resource_id),
  ])

  return resources.filter((r) => !excludedIds.has(r.id))
}
